import React, { useState } from "react";
import { ChevronLeft, Maximize2 } from "lucide-react";
import Dropdown, { Options } from "@/components/ui/DropDown";
import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import TextareaField from "@/components/ui/Textareafield";
import Checkbox from "@/components/ui/CheckBox";
import { useNavigate } from "react-router-dom";

// Type definitions
interface Asset {
  id: string;
  name: string;
  isActive: boolean;
}

interface AdSpecs {
  language: string;
  region: string;
  format: string;
  minSize: string;
  fileType: string;
}

interface ChangeLogEntry {
  timestamp: string;
  description: string;
}

interface AdBuilderProps {
  campaignName?: string;
  categoryName?: string;
  adType?: string;
  dueDate?: string;
  onSave?: (data: AdBuilderData) => void;
  onSubmit?: (data: AdBuilderData) => void;
}

interface AdBuilderData {
  adTitle: string;
  conversionLocation: string;
  cta: string;
  landingPageUrl: string;
  utm: string;
  primaryText: string;
  headline: string;
  description: string;
  linkAcrossVersions: {
    primaryText: boolean;
    headline: boolean;
    description: boolean;
  };
}

// Asset badge component
const AssetBadge: React.FC<{
  number: number;
  name: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ number, name, isActive, onClick }) => {
  const bgColor = isActive ? "bg-orange-400" : "bg-orange-200";
  const textColor = isActive ? "text-white" : "text-gray-600";

  return (
    <button
      onClick={onClick}
      className={`${bgColor} ${textColor} px-4 py-2 rounded-full flex items-center gap-2 transition-all hover:opacity-80`}
    >
      <span className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-orange-500 font-semibold text-sm">
        {number}
      </span>
      <span className="font-medium">{name}</span>
    </button>
  );
};

const AdBuilder: React.FC<AdBuilderProps> = ({
  campaignName = "Awareness",
  categoryName = "Social",
  adType = "Facebook Feed Image",
  dueDate = "05/06/2024",
  onSave,
  onSubmit,
}) => {
  // State management
  const [assets] = useState<Asset[]>([
    { id: "1", name: "Tic Tac Tea", isActive: true },
    { id: "2", name: "Spicy T", isActive: false },
    { id: "3", name: "Tea Time", isActive: false },
    { id: "4", name: "Asset 4", isActive: false },
    { id: "5", name: "Asset 5", isActive: false },
  ]);

  const [activeAsset, setActiveAsset] = useState("1");
  const [adTitle, setAdTitle] = useState("Tic Tac Tea");
  const [showFullScreen, setShowFullScreen] = useState(false);

  // Form state
  const [conversionLocation, setConversionLocation] = useState("");
  const [cta, setCta] = useState("");
  const [landingPageUrl, setLandingPageUrl] = useState("");
  const [utm, setUtm] = useState("");
  const [primaryText, setPrimaryText] = useState("");
  const [headline, setHeadline] = useState("");
  const [description, setDescription] = useState("");

  const navigate = useNavigate();

  // Link across versions checkboxes
  const [linkAcrossVersions, setLinkAcrossVersions] = useState({
    primaryText: true,
    headline: true,
    description: false,
  });

  // Dropdown options
  const conversionLocationOptions: Options[] = [
    { title: "On your ad", value: "on-your-ad" },
    { title: "Website", value: "website" },
    { title: "App", value: "app" },
    { title: "Messenger", value: "messenger" },
  ];

  const ctaOptions: Options[] = [
    { title: "Shop Now", value: "shop-now" },
    { title: "Learn More", value: "learn-more" },
    { title: "Sign Up", value: "sign-up" },
    { title: "Download", value: "download" },
    { title: "Contact Us", value: "contact-us" },
  ];

  // Ad specs data
  const adSpecs: AdSpecs = {
    language: "EN",
    region: "National",
    format: "1:1, 1.91:1",
    minSize: "1080×1080px",
    fileType: "JPG, PNG",
  };

  // Change log data
  const changeLog: ChangeLogEntry[] = [
    {
      timestamp: "01/03/2024 3:34pm",
      description: "Brad Wilton edited budget and CTA.",
    },
    {
      timestamp: "01/03/2024 2:36pm",
      description: "Danielle Autran edited headline and description.",
    },
    {
      timestamp: "01/01/2024 12:05pm",
      description: "Brad Wilton added team members to campaign.",
    },
    {
      timestamp: "01/01/2024 12:03pm",
      description: "Brad Wilton created asset.",
    },
  ];

  const handleSaveChanges = () => {
    const data: AdBuilderData = {
      adTitle,
      conversionLocation,
      cta,
      landingPageUrl,
      utm,
      primaryText,
      headline,
      description,
      linkAcrossVersions,
    };
    onSave?.(data);
  };

  const handleSubmitForReview = () => {
    const data: AdBuilderData = {
      adTitle,
      conversionLocation,
      cta,
      landingPageUrl,
      utm,
      primaryText,
      headline,
      description,
      linkAcrossVersions,
    };
    onSubmit?.(data);
  };

  return (
    <div>
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                navigate(-1);
              }}
              className="text-pink-500 hover:text-pink-600 transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <h1 className="text-2xl">
              <span className="font-semibold text-gray-900">
                {campaignName}
              </span>{" "}
              / {categoryName}
            </h1>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-sm text-gray-600 mb-1">Ad Type</div>
            <div className="text-xl font-semibold text-gray-900">{adType}</div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600 mb-1">Due to Publisher</div>
            <div className="text-xl font-semibold text-gray-900">{dueDate}</div>
          </div>
        </div>
      </div>

      <hr className="border-gray-300 my-5" />

      {/* Assets Tabs */}
      <div className="border-b border-gray-200 px-6 py-2">
        <div className="flex items-center gap-3 overflow-x-auto">
          {assets.map((asset, index) => (
            <AssetBadge
              key={asset.id}
              number={index + 1}
              name={asset.name}
              isActive={activeAsset === asset.id}
              onClick={() => setActiveAsset(asset.id)}
            />
          ))}
        </div>
      </div>

      {/* Asset Selection */}
      <div className="bg-orange-400 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <span className="text-white font-medium mr-2">Ad Title</span>
            <input
              type="text"
              value={adTitle}
              onChange={(e) => setAdTitle(e.target.value)}
              className="flex-1 max-w-md px-4 py-2 rounded-lg border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white/40"
              placeholder="Enter ad title"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex text-white">In progress</div>

            <Button type="outline">Submit for Review</Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6">
        <div>
          {/* Left Column - Preview */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setShowFullScreen(!showFullScreen)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Maximize2 size={16} />
                <span className="text-sm font-medium">View Full Screen</span>
              </button>
              <Button type="primary" onClick={handleSaveChanges}>
                Save Changes
              </Button>
            </div>

            {/* Phone Mockup */}
            <div className="bg-white rounded-3xl shadow-lg p-4 border-4 border-pink-300 max-w-sm mx-auto">
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                    C
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      Client Name
                    </div>
                    <div className="text-xs text-gray-500">Sponsored</div>
                  </div>
                  <button className="text-gray-400">
                    <svg
                      width="4"
                      height="16"
                      viewBox="0 0 4 16"
                      fill="currentColor"
                    >
                      <circle cx="2" cy="2" r="2" />
                      <circle cx="2" cy="8" r="2" />
                      <circle cx="2" cy="14" r="2" />
                    </svg>
                  </button>
                </div>

                {/* Primary Text */}
                <div className="px-4 pb-3">
                  <p className="text-sm text-gray-700">
                    {primaryText || "Primary text placeholder."}
                  </p>
                </div>

                {/* Image */}
                <div className="bg-gray-200 aspect-square flex items-center justify-center">
                  <svg
                    className="w-16 h-16 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>

                {/* Bottom Section */}
                <div className="p-4 bg-gray-50">
                  <div className="text-xs text-gray-500 mb-1">
                    destinationurl.com
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm text-gray-900">
                      {headline || "Headline Placeholder to go here."}
                    </div>
                    <div className="bg-gray-200 px-3 py-1 rounded text-xs font-semibold text-gray-700">
                      CTA
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="space-y-6 mt-14">
            {/* Ad Specs */}
            <div className="">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Ad Specs
                </h2>
                <a
                  href="#"
                  className="text-sm text-gray-400 hover:text-gray-600"
                >
                  Link to full specs
                </a>
              </div>
              <div className="grid grid-cols-5 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Language
                  </div>
                  <div className="text-gray-900">{adSpecs.language}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Region
                  </div>
                  <div className="text-gray-900">{adSpecs.region}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Format
                  </div>
                  <div className="text-gray-900">{adSpecs.format}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Min. Size
                  </div>
                  <div className="text-gray-900">{adSpecs.minSize}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    File Type
                  </div>
                  <div className="text-gray-900">{adSpecs.fileType}</div>
                </div>
              </div>
            </div>

            {/* Objectives */}
            <div className="mt-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Objectives
              </h2>

              <hr className="border-gray-300 mb-5" />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Conversion Location
                  </label>
                  <Dropdown
                    selected={conversionLocation}
                    setSelected={setConversionLocation}
                    options={conversionLocationOptions}
                    placeholder="On your ad"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    CTA
                  </label>
                  <Dropdown
                    selected={cta}
                    setSelected={setCta}
                    options={ctaOptions}
                    placeholder="Shop Now"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Landing Page URL
                  </label>
                  <TextField
                    type="url"
                    value={landingPageUrl}
                    onChange={(e) => setLandingPageUrl(e.target.value)}
                    placeholder="Enter valid URL"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    UTM
                  </label>
                  <TextField
                    type="text"
                    value={utm}
                    onChange={(e) => setUtm(e.target.value)}
                    placeholder="Enter valid URL"
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Content
              </h2>

              <hr className="border-gray-300 mb-5" />

              <div className="grid grid-cols-2 space-x-4">
                {/* Primary Text */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-900">
                      Primary Text
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-500">
                      <span>Link across versions</span>
                      <Checkbox
                        checked={linkAcrossVersions.primaryText}
                        onChange={(e) =>
                          setLinkAcrossVersions((prev) => ({
                            ...prev,
                            primaryText: e,
                          }))
                        }
                      />
                    </label>
                  </div>
                  <TextareaField
                    value={primaryText}
                    onChange={(e) => setPrimaryText(e.target.value)}
                    placeholder="Placeholder text...."
                    rows={4}
                    maxLength={125}
                    className="w-full"
                  />

                  <div className="text-right text-xs text-gray-400 mt-1">
                    {primaryText.length}/125
                  </div>
                </div>

                <div>
                  {/* Headline */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-900">
                        Headline
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="text-xs">Link across versions</span>
                        <Checkbox
                          checked={linkAcrossVersions.headline}
                          onChange={(e) =>
                            setLinkAcrossVersions((prev) => ({
                              ...prev,
                              headline: e,
                            }))
                          }
                        />
                      </label>
                    </div>
                    <TextField
                      type="text"
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      placeholder="Placeholder text...."
                      maxLength={27}
                      className="w-full"
                    />
                    <div className="text-right text-xs text-gray-400 mt-1">
                      {headline.length}/27
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-900">
                        Description
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="text-xs">Link across versions</span>
                        <Checkbox
                          checked={linkAcrossVersions.description}
                          onChange={(e) =>
                            setLinkAcrossVersions((prev) => ({
                              ...prev,
                              description: e,
                            }))
                          }
                        />
                      </label>
                    </div>

                    <TextField
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Placeholder text...."
                      maxLength={27}
                      className="w-full"
                    />

                    <div className="text-right text-xs text-gray-400 mt-1">
                      {description.length}/27
                    </div>
                  </div>
                </div>

                {/* Media */}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-900 mb-2">
                Media
              </div>
              <div className="text-sm text-gray-500 mb-3">1 Image</div>
              <div className="flex items-start gap-3">
                <div className="relative">
                  <div className="text-xs text-gray-500 mb-2">1</div>
                  <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                    <svg
                      className="w-8 h-8 text-gray-400 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span className="text-xs text-gray-500">
                      Image (30MB max)
                    </span>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-600 transition-colors mt-6">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <hr className="border-gray-300 mb-5" />

            {/* Change Log */}
            <div className="">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Change Log
              </h2>
              <div className="space-y-3">
                {changeLog.map((entry, index) => (
                  <div key={index} className="flex gap-4 text-sm">
                    <div className="text-gray-500 whitespace-nowrap flex-shrink-0">
                      {entry.timestamp}
                    </div>
                    <div className="text-gray-700">{entry.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdBuilder;
